import {
  Autocomplete,
  Box,
  InputBase,
  InputBaseComponentProps,
  InputLabel,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { Button, PlusIcon } from "components";
import React from "react";
import { Controller } from "react-hook-form";

interface CustomerUserAutocompleteProps {
  onChange?: (event: any, newValue: any) => void;
  onInputChange?: (event: any, newInputValue: any) => void;
  inputProps?: InputBaseComponentProps;
  options: Array<Record<string, any>>;
  label?: string;
  placeholder: string;
  value?: any;
  fullWidth?: boolean;
  labelMappingKey?: string;
  valueMappingKey?: string;
  getOptionLabel?: (option: Record<string, any> | string) => string;
  inputStyle?: SxProps<Theme>;
  control: any;
  name: string;
  freeSolo?: boolean;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  renderOptions?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: any,
  ) => React.ReactNode;
  multiple?: boolean;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  nodataText?: string;
  filterSelectedOptions?: boolean;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  isCreatable?: boolean;
  onCreateClick?: () => void;
  createButtonText?: string;
  onRefreshClick?: () => void;
  isRefreshable?: boolean;
  deleteItemOnBackspace?: boolean;
  filterOptions?: (options: any, state: any) => Array<any>;
}
export const CreateTicketAutocomplete = ({
  options,
  onInputChange,
  inputProps,
  onChange,
  label,
  placeholder,
  value,
  fullWidth = false,
  labelMappingKey,
  valueMappingKey,
  getOptionLabel,
  inputStyle,
  control,
  name,
  freeSolo,
  renderOptions,
  onBlur,
  multiple,
  required = false,
  error,
  errorMessage,
  disabled,
  nodataText,
  filterSelectedOptions,
  isOptionEqualToValue,
  isCreatable = false,
  onCreateClick,
  createButtonText = "Add",
  onRefreshClick,
  isRefreshable = false,
  deleteItemOnBackspace = true,
  filterOptions,
}: CustomerUserAutocompleteProps) => {
  const theme = useTheme();

  const handleBackspaceKeydown = (event: any) => {
    if (!deleteItemOnBackspace) {
      if (event.key === "Backspace" || event.key === "Delete") {
        event.stopPropagation();
      }
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={[]}
      render={({ field }) => (
        <Autocomplete
          {...field}
          {...(filterOptions && { filterOptions })}
          multiple={multiple}
          disabled={disabled}
          filterSelectedOptions={filterSelectedOptions}
          freeSolo={freeSolo}
          isOptionEqualToValue={isOptionEqualToValue}
          getOptionLabel={
            getOptionLabel
              ? getOptionLabel
              : (option) =>
                  typeof option === "string"
                    ? option
                    : option[`${labelMappingKey}`]
          }
          options={options}
          onBlur={onBlur}
          fullWidth={fullWidth}
          autoComplete={false}
          noOptionsText={
            <Typography variant="body-small">
              {nodataText ? nodataText : "No options"}
            </Typography>
          }
          onChange={(event: any, newValue: any) => {
            field.onChange(newValue);
            onChange && onChange(event, newValue);
          }}
          onInputChange={onInputChange}
          PaperComponent={({ children }) => (
            <Box
              sx={{
                mt: "10px",
                backgroundColor: "#FFF",
                borderRadius: "8px",
                // boxShadow: "none",
                boxShadow:
                  "0px 0px 8px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.08)",
              }}
            >
              {isCreatable && (
                <Stack
                  sx={{
                    cursor: "pointer",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: "24px",
                    py: "8px",
                    ...theme.typography["body-small"],
                    color: theme.palette.lightBg.low,
                    borderBottom: `1px solid ${theme.palette.neutral["015"]}`,
                  }}
                  onMouseDown={() => onCreateClick && onCreateClick()}
                >
                  {createButtonText}
                  <PlusIcon fontSize="small" />
                </Stack>
              )}

              {children}
              {isRefreshable && (
                <Stack
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",

                    px: "24px",
                    py: "10px",
                    ...theme.typography["body-small"],
                    color: theme.palette.lightBg.low,
                    borderTop: `1px solid ${theme.palette.neutral["015"]}`,
                  }}
                >
                  <Typography
                    variant="body-small"
                    color={theme.palette.primary.main}
                    sx={{
                      cursor: "pointer",
                      transition: "color 0.3s",
                      ":hover": {
                        color: theme.palette.primary.pressed,
                      },
                    }}
                    onMouseDown={() => onRefreshClick && onRefreshClick()}
                  >
                    Refresh
                  </Typography>
                </Stack>
              )}
            </Box>
          )}
          slotProps={{
            popper: {
              sx: { boxShadow: "none" },
            },
            paper: {
              sx: {
                mt: "10px",
                backgroundColor: "#FFF",
                borderRadius: "8px",
                // boxShadow: "none",
                boxShadow:
                  "0px 0px 8px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.08)",
              },
            },
          }}
          renderInput={(params) => (
            <>
              {label && (
                <InputLabel
                  sx={{
                    color: theme.palette.lightBg.medium,
                    ...theme.typography["caption-2"],
                    paddingLeft: "16px",
                    minWidth: "180px",
                  }}
                >
                  {label}
                  {required && <span style={{ color: "red" }}>*</span>}
                </InputLabel>
              )}

              <InputBase
                onKeyDown={handleBackspaceKeydown}
                ref={params.InputProps.ref}
                placeholder={placeholder}
                sx={{
                  ...theme.typography["body-medium"],
                  paddingLeft: "16px",
                  borderRadius: "4px",

                  border: error
                    ? `1px solid ${theme.palette.error.main}`
                    : "inherit",
                  ...inputStyle,
                }}
                inputProps={{ ...params.inputProps, ...inputProps }}
                autoFocus
                fullWidth
              />

              {error && (
                <Box>
                  <Typography
                    variant="body-small"
                    color="error"
                    sx={{ padding: "0 4px 0px 0px" }}
                  >
                    {errorMessage}
                  </Typography>
                </Box>
              )}
            </>
          )}
          //   PopperComponent={CustomDropdown}
          renderOption={
            renderOptions
              ? renderOptions
              : (props, option) => {
                  return (
                    <li
                      {...props}
                      key={option[`${valueMappingKey}`]}
                      style={{
                        ...theme.typography["body-small"],
                        display: "flex",
                        flexDirection: "row",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      {option[`${labelMappingKey}`]}
                    </li>
                  );
                }
          }
        />
      )}
    />
  );
};
